/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { secondsExtendedRegexp } from '../DateTimeDefault';
import { numberToSimpleTime } from '../DateTimeHelper';
import { parseForDateTimeFormat, parseForNumberFormat, TokenType } from './parser';
export function format(value, formatArg, config, dateHelper) {
    const tryDateTime = config.stringifyDateTime(dateHelper.numberToSimpleDateTime(value), formatArg); // default points to defaultStringifyDateTime()
    if (tryDateTime !== undefined) {
        return tryDateTime;
    }
    const tryDuration = config.stringifyDuration(numberToSimpleTime(value), formatArg);
    if (tryDuration !== undefined) {
        return tryDuration;
    }
    const expression = parseForNumberFormat(formatArg);
    if (expression !== undefined) {
        return numberFormat(formatArg, value);
    }
    return formatArg;
}
export function padLeft(number, size) {
    let result = number + '';
    while (result.length < size) {
        result = '0' + result;
    }
    return result;
}
export function padRight(number, size) {
    let result = number + '';
    while (result.length < size) {
        result = result + '0';
    }
    return result;
}
function countChars(text, char) {
    return text.split(char).length - 1;
}
const maskRegex = /[0-9\-+#]/;
const notMaskRegex = /[^\d\-+#]/g;
function getIndex(mask) {
    return mask.search(maskRegex);
}
function processMask(mask = '#.##') {
    const maskObj = {
        prefix: '',
        suffix: '',
        mask: '',
        maskHasNegativeSign: true,
        maskHasPositiveSign: true,
        decimal: '',
        separator: '',
        integer: '',
        fraction: ''
    };
    const len = mask.length;
    const start = getIndex(mask);
    maskObj.prefix = start > 0 ? mask.substring(0, start) : '';
    // Reverse string: not an ideal method if there are surrogate pairs
    const end = getIndex(mask.split('').reverse().join(''));
    const offset = len - end;
    const substr = mask.substring(offset, offset + 1);
    // Add 1 to offset if mask has a trailing decimal/comma
    const indx = offset + ((substr === '.' || (substr === ',')) ? 1 : 0);
    maskObj.suffix = end > 0 ? mask.substring(indx, len) : '';
    maskObj.mask = mask.substring(start, indx);
    maskObj.maskHasNegativeSign = maskObj.mask.startsWith('-');
    maskObj.maskHasPositiveSign = maskObj.mask.startsWith('+');
    // Search for group separator & decimal; anything not digit,
    // not +/- sign, and not #
    let result = maskObj.mask.match(notMaskRegex);
    // Treat the right most symbol as decimal
    maskObj.decimal = (result && result[result.length - 1]) || '.';
    // Treat the left most symbol as group separator
    maskObj.separator = (result && result[1] && result[0]) || ',';
    // Split the decimal for the format string if any
    result = maskObj.mask.split(maskObj.decimal);
    maskObj.integer = result[0];
    maskObj.fraction = result[1];
    return maskObj;
}
function processValue(value, maskObj, options) {
    let isNegative = false;
    const valObj = {
        value,
        sign: '',
        integer: '',
        fraction: '',
        result: ''
    };
    if (value < 0) {
        isNegative = true;
        // Process only abs(), and turn on flag.
        valObj.value = -valObj.value;
    }
    valObj.sign = isNegative ? '-' : '';
    // Fix the decimal first, toFixed will auto fill trailing zero.
    valObj.value = Number(valObj.value).toFixed(maskObj.fraction && maskObj.fraction.length);
    // Convert number to string to trim off *all* trailing decimal zero(es)
    valObj.value = Number(valObj.value).toString();
    // Fill back any trailing zero according to format
    // look for last zero in format
    const posTrailZero = maskObj.fraction && maskObj.fraction.lastIndexOf('0');
    let [valInteger = '0', valFraction = ''] = valObj.value.split('.');
    if (!valInteger) {
        valInteger = '0';
    }
    if (!valFraction || (valFraction && valFraction.length <= posTrailZero)) {
        valFraction = posTrailZero < 0
            ? ''
            : (Number('0.' + valFraction).toFixed(posTrailZero + 1)).replace('0.', '');
    }
    valObj.integer = valInteger;
    valObj.fraction = valFraction;
    addSeparators(valObj, maskObj);
    // Remove negative sign if result is zero
    if (valObj.result === '0' || valObj.result === '') {
        // Remove negative sign if result is zero
        isNegative = false;
        valObj.sign = '';
    }
    if (!isNegative && maskObj.maskHasPositiveSign) {
        valObj.sign = '+';
    }
    else if (isNegative && maskObj.maskHasPositiveSign) {
        valObj.sign = '-';
    }
    else if (isNegative) {
        valObj.sign = options && options.enforceMaskSign && !maskObj.maskHasNegativeSign
            ? ''
            : '-';
    }
    return valObj;
}
function addSeparators(valObj, maskObj) {
    valObj.result = '';
    // Look for separator
    const szSep = maskObj.integer.split(maskObj.separator);
    // Join back without separator for counting the pos of any leading 0
    const maskInteger = szSep.join('');
    const posLeadZero = maskInteger && maskInteger.indexOf('0');
    if (posLeadZero > -1) {
        while (valObj.integer.length < (maskInteger.length - posLeadZero)) {
            valObj.integer = '0' + valObj.integer;
        }
    }
    else if (Number(valObj.integer) === 0) {
        valObj.integer = '';
    }
    // Process the first group separator from decimal (.) only, the rest ignore.
    // get the length of the last slice of split result.
    const posSeparator = (szSep[1] && szSep[szSep.length - 1].length);
    if (posSeparator) {
        const len = valObj.integer.length;
        const offset = len % posSeparator;
        for (let indx = 0; indx < len; indx++) {
            valObj.result += valObj.integer.charAt(indx);
            // -posSeparator so that won't trail separator on full length
            if (!((indx - offset + 1) % posSeparator) && indx < len - posSeparator) {
                valObj.result += maskObj.separator;
            }
        }
    }
    else {
        valObj.result = valObj.integer;
    }
    valObj.result += (maskObj.fraction && valObj.fraction)
        ? maskObj.decimal + valObj.fraction
        : '';
    return valObj;
}
function numberFormat(token, value) {
    let result = '';
    const maskObj = processMask(token);
    const valObj = processValue(value, maskObj, {});
    return result = (maskObj.prefix + valObj.sign + valObj.result + maskObj.suffix);
}
export function defaultStringifyDuration(time, formatArg) {
    const expression = parseForDateTimeFormat(formatArg);
    if (expression === undefined) {
        return undefined;
    }
    const tokens = expression.tokens;
    let result = '';
    for (const token of tokens) {
        if (token.type === TokenType.FREE_TEXT) {
            result += token.value;
            continue;
        }
        if (secondsExtendedRegexp.test(token.value)) {
            const fractionOfSecondPrecision = token.value.length - 3;
            result += (time.seconds < 10 ? '0' : '') + Math.round(time.seconds * Math.pow(10, fractionOfSecondPrecision)) / Math.pow(10, fractionOfSecondPrecision);
            continue;
        }
        switch (token.value.toLowerCase()) {
            case 'h':
            case 'hh': {
                result += padLeft(time.hours, token.value.length);
                time.hours = 0;
                break;
            }
            case '[hh]': {
                result += padLeft(time.hours, token.value.length - 2);
                time.hours = 0;
                break;
            }
            case 'm':
            case 'mm': {
                result += padLeft(time.minutes, token.value.length);
                time.minutes = 0;
                break;
            }
            case '[mm]': {
                result += padLeft(time.minutes + 60 * time.hours, token.value.length - 2);
                time.minutes = 0;
                time.hours = 0;
                break;
            }
            /* seconds */
            case 's':
            case 'ss': {
                result += padLeft(time.seconds, token.value.length);
                break;
            }
            default: {
                return undefined;
            }
        }
    }
    return result;
}
export function defaultStringifyDateTime(dateTime, formatArg) {
    const expression = parseForDateTimeFormat(formatArg);
    if (expression === undefined) {
        return undefined;
    }
    const tokens = expression.tokens;
    let result = '';
    let minutes = false;
    const ampm = tokens.some((token) => token.type === TokenType.FORMAT &&
        (token.value === 'a/p' || token.value === 'A/P' || token.value === 'am/pm' || token.value === 'AM/PM'));
    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.type === TokenType.FREE_TEXT) {
            result += token.value;
            continue;
        }
        if (secondsExtendedRegexp.test(token.value)) {
            const fractionOfSecondPrecision = token.value.length - 3;
            result += (dateTime.seconds < 10 ? '0' : '') + Math.round(dateTime.seconds * Math.pow(10, fractionOfSecondPrecision)) / Math.pow(10, fractionOfSecondPrecision);
            continue;
        }
        switch (token.value.toLowerCase()) {
            /* hours*/
            case 'h':
            case 'hh': {
                minutes = true;
                result += padLeft(ampm ? (dateTime.hours + 11) % 12 + 1 : dateTime.hours, token.value.length);
                break;
            }
            /* days */
            case 'd':
            case 'dd': {
                result += padLeft(dateTime.day, token.value.length);
                break;
            }
            /* seconds */
            case 's':
            case 'ss': {
                result += padLeft(Math.round(dateTime.seconds), token.value.length);
                break;
            }
            /* minutes / months */
            case 'm':
            case 'mm': {
                if (i + 1 < tokens.length && tokens[i + 1].value.startsWith(':')) {
                    minutes = true;
                }
                if (minutes) {
                    result += padLeft(dateTime.minutes, token.value.length);
                }
                else {
                    result += padLeft(dateTime.month, token.value.length);
                }
                minutes = true;
                break;
            }
            /* years */
            case 'yy': {
                result += padLeft(dateTime.year % 100, token.value.length);
                break;
            }
            case 'yyyy': {
                result += dateTime.year;
                break;
            }
            /* AM / PM */
            case 'am/pm':
            case 'a/p': {
                const [am, pm] = token.value.split('/');
                result += dateTime.hours < 12 ? am : pm;
                break;
            }
            default: {
                return undefined;
            }
        }
    }
    return result;
}
